/**
 * User Menu Component - SeeTex Web
 * 
 * Displays user info and logout button for authenticated users
 */

import { useState, useRef, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { useAuth } from "../contexts/AuthContext";
import { clearAllLocalData } from "../services/file-sync";

const API_BASE = (import.meta.env.VITE_COMPILE_API_URL as string | undefined) ?? "/api";

export function UserMenu({ onUpgrade, onSignUp }: { onUpgrade?: () => void; onSignUp?: () => void } = {}) {
    const { user, logout, loading, isDemoMode, isGuest } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [billingError, setBillingError] = useState<string | false>(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Close menu on escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen]);

    if (!user) return null;

    const displayName = user.isAnonymous ? "Guest" : (user.displayName || user.email?.split("@")[0] || "User");
    const avatarLetter = user.isAnonymous ? "G" : displayName.charAt(0).toUpperCase();

    const handleLogout = async () => {
        setIsOpen(false);
        await logout();
    };

    const handleBilling = async () => {
        setIsOpen(false);
        setBillingError(false);
        try {
            const token = await getAuth().currentUser?.getIdToken();
            if (!token) { setBillingError("Not signed in"); return; }
            const res = await fetch(`${API_BASE}/stripe/portal`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ uid: user.uid }),
            });
            const data = await res.json();
            if (data.url) {
                window.location.href = data.url;
            } else if (res.status === 404) {
                setBillingError("No active subscription — subscribe to Pro first");
            } else {
                setBillingError("Billing portal unavailable — try again later");
            }
        } catch {
            setBillingError("Billing portal unavailable — try again later");
        }
    };

    const handleDeleteAccount = async () => {
        if (!user) return;
        setDeleting(true);
        try {
            const token = await getAuth().currentUser?.getIdToken();
            if (!token) throw new Error("Not signed in");
            const res = await fetch(`${API_BASE}/account/delete`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ uid: user.uid }),
            });
            if (res.ok) {
                clearAllLocalData();
                await logout();
            } else {
                const data = await res.json();
                setBillingError(data.error ?? "Failed to delete account");
            }
        } catch {
            setBillingError("Failed to delete account — try again later");
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <div className="user-menu" ref={menuRef}>
            {billingError && (
                <div className="user-menu-error" onClick={() => setBillingError(false)}>
                    {billingError} ✕
                </div>
            )}
            <button
                className="user-menu-trigger"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <span className="user-avatar">{avatarLetter}</span>
                <span className="user-name">{displayName}</span>
                {isDemoMode && <span className="user-demo-badge">Demo</span>}
                {isGuest && <span className="user-guest-badge">Guest</span>}
            </button>

            {isOpen && (
                <div className="user-menu-dropdown" role="menu">
                    <div className="user-menu-header">
                        <span className="user-avatar-large">{avatarLetter}</span>
                        <div className="user-menu-info">
                            <span className="user-menu-name">{displayName}</span>
                            <span className="user-menu-email">{user.isAnonymous ? "Not signed in" : user.email}</span>
                        </div>
                    </div>
                    <hr />
                    {isGuest && onSignUp && (
                        <button
                            className="user-menu-item user-menu-item--accent"
                            onClick={() => { setIsOpen(false); onSignUp(); }}
                            role="menuitem"
                        >
                            Create account to save work
                        </button>
                    )}
                    {!isGuest && onUpgrade && (
                        <button
                            className="user-menu-item"
                            onClick={() => { setIsOpen(false); onUpgrade(); }}
                            role="menuitem"
                        >
                            Upgrade to Pro
                        </button>
                    )}
                    {!isGuest && (
                        <button
                            className="user-menu-item"
                            onClick={handleBilling}
                            role="menuitem"
                        >
                            Manage billing
                        </button>
                    )}
                    <hr />
                    <button
                        className="user-menu-item"
                        onClick={handleLogout}
                        disabled={loading}
                        role="menuitem"
                    >
                        {loading ? "Signing out..." : "Sign out"}
                    </button>
                    {!isGuest && (
                        <>
                            <hr />
                            <button
                                className="user-menu-item user-menu-item--danger"
                                onClick={() => { setIsOpen(false); setShowDeleteConfirm(true); }}
                                role="menuitem"
                            >
                                Delete account
                            </button>
                        </>
                    )}
                </div>
            )}

            {showDeleteConfirm && (
                <div className="confirm-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="confirm-modal-title">Delete your account?</h3>
                        <p className="confirm-modal-message">
                            This will permanently delete your account, all documents, and cancel any active subscription. This cannot be undone.
                        </p>
                        <div className="confirm-modal-actions">
                            <button className="confirm-modal-cancel" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</button>
                            <button className="confirm-modal-confirm" onClick={handleDeleteAccount} disabled={deleting}>
                                {deleting ? "Deleting..." : "Delete permanently"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
